/* ──────────────────────────────────────────────────────────────────────────
 *  Analytics core — provider registry + dispatch functions.
 *
 *  Usage:
 *    import { analytics } from "@/lib/analytics/core";
 *    analytics.track("add_to_cart", { currency: "CAD", value: 29, items: [...] });
 *
 *  Adding a new provider:
 *    1. Create a file implementing AnalyticsAdapter.
 *    2. Call analytics.register(myAdapter) during app init.
 *    3. All existing track() calls automatically flow to the new adapter.
 * ────────────────────────────────────────────────────────────────────────── */

import type {
  AnalyticsAdapter,
  AnalyticsEventName,
  AnalyticsEventMap,
} from "./types";
import { hasAnalyticsConsent, CONSENT_EVENT } from "./types";

const DEBUG =
  typeof process !== "undefined" &&
  process.env.NODE_ENV === "development";

class AnalyticsCore {
  private adapters: AnalyticsAdapter[] = [];
  private initialized = false;
  private queue: Array<() => void> = [];

  register(adapter: AnalyticsAdapter) {
    if (this.adapters.some((a) => a.name === adapter.name)) return;
    this.adapters.push(adapter);
    if (this.initialized) {
      adapter.init();
      this.flush();
    }
  }

  /**
   * Call after consent is confirmed (or immediately if consent popup is off).
   * Initialises every registered adapter and replays the queued events.
   */
  init() {
    if (this.initialized) return;
    this.initialized = true;
    for (const adapter of this.adapters) {
      try {
        adapter.init();
      } catch (err) {
        if (DEBUG) console.warn(`[analytics] ${adapter.name} init failed`, err);
      }
    }
    this.flush();
  }

  track<E extends AnalyticsEventName>(
    event: E,
    properties: AnalyticsEventMap[E],
  ) {
    if (!this.initialized) {
      this.queue.push(() => this.track(event, properties));
      return;
    }
    if (!hasAnalyticsConsent()) return;

    if (DEBUG) {
      console.debug(`[analytics] ${event}`, properties);
    }

    for (const adapter of this.adapters) {
      try {
        adapter.track(event, properties);
      } catch (err) {
        if (DEBUG)
          console.warn(`[analytics] ${adapter.name} track error`, err);
      }
    }
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    if (!hasAnalyticsConsent()) return;
    for (const adapter of this.adapters) {
      try {
        adapter.identify?.(userId, traits);
      } catch {
        /* swallow */
      }
    }
  }

  reset() {
    if (!hasAnalyticsConsent()) return;
    for (const adapter of this.adapters) {
      try {
        adapter.reset?.();
      } catch {
        /* swallow */
      }
    }
  }

  private flush() {
    const pending = [...this.queue];
    this.queue = [];
    for (const fn of pending) fn();
  }
}

/** Singleton shared across the app. */
export const analytics = new AnalyticsCore();

/* ── Auto-init on consent change ───────────────────────────────────────── */

if (typeof window !== "undefined") {
  if (hasAnalyticsConsent()) {
    analytics.init();
  } else {
    const onConsent = () => {
      if (hasAnalyticsConsent()) {
        analytics.init();
        window.removeEventListener(CONSENT_EVENT, onConsent);
      }
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
  }
}
